import { Box, Button, Colors, Text } from '@fozito/common-ui';
import { Skeleton } from 'antd';
import cashierService from 'modules/POS/services';
import { IProductItem } from 'modules/shop/types/product';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { cashierCtrl } from '../cashier.controller';
import { IUISelectItem } from '../cashier.interface';
import empty_product_img from './empty-product.svg';

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  padding: 8px;
`;

const ProductCard = styled(Button)`
  overflow: hidden;
  transition: all 0.2s ease;
  display: flex;
  padding: 8px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 4px;
  border: 1.5px solid #232F3E;
  background: #FFF;
  position: relative;

  &:hover {
    border-color: ${Colors.secondary[3]};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: ${Colors.secondary[1]};
  }
  &:focus {
    border-radius: 4px;
    border: 1px solid #232F3E;
    background: #E8FBFF;
  }

  .custom-text.color_text_1.content_content_3_16_semibold {
    align-self: stretch;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &:hover,
  &:active {
    opacity: 0.8;
    transform: scale(1.05);
    transition: all 0.1s;
  }

  &:disabled {
    opacity: 0.7;
    background: #FFF;
    &:hover,
    &:active {
      opacity: 1;
      cursor: not-allowed;
      transform: none;
      transition: none;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  flex: 1;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 64px;
  object-fit: cover;
  border-radius: 5px;
  background: #eee;
`;

const ProductInfo = styled.div`
  flex: 1;
  padding-top: 8px;
  text-align: start;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ProductName = styled(Text)`
  color: ${Colors.neutral.text[5]};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductPriceInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 3px;
`;

const ProductQuantity = styled(Text)`
  font-style: italic !important;
  color: #505050;
  font-family: Poppins;
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
`;
const DiscountText = styled(Text)`
color: #d2464f;
text-align: right;
font-family: Poppins;
font-size: 16px;
font-style: normal;
line-height: normal;
text-decoration-line: strikethrough;
text-transform: capitalize;
text-decoration: line-through;
font-weight: bold;
`;

const SoldOut = styled(Text)`
  background-color: ${Colors.neutral.bg[6]};
  padding: 2px 2px;
  position: absolute;
  z-index: 9;
  bottom: -10px;
  left: -2px;
  border-radius: 2px;
`;
const DiscountStyled = styled.div`
display: flex;
width: 32px;
height: 32px;
padding: 0;
flex-direction: column;
justify-content: center;
align-items: center;
gap: 10px;
aspect-ratio: 1/1;
position: absolute;
right: 3.333px;
top: 3.653px;
z-index: 9;
border-radius: 100px;
background: #d2464f;
`;
interface Props {
  value?: IUISelectItem[];
  onChange?: (value: IUISelectItem[]) => void;
  products: IProductItem[];
}

const ProductsList = ({ value = [], products = [] }: Props) => {
  const isLoading = cashierService.selectors.isProductLoading();
  const searchProducts = cashierService.selectors.searchProducts();
  const textSearch = cashierService.selectors.textSearch();

  const onChooseProduct = (product: IProductItem) => () => cashierCtrl.chooseProduct(product);
  const renderPrice = (item: IProductItem) => {
    if (!item.discount) return (
      <Text title_h7_20_semibold>
        {formatCurrency(item.sellPrice)}
      </Text>
    );

    return <>
      <DiscountText color={Colors.primary[6]}>
        {formatCurrency(item.sellPrice)}
      </DiscountText>
      <Text title_h7_20_semibold>
        {formatCurrency(item.sellPrice - item.discount)}
      </Text>
    </>;
  };
  const calcDiscountPercent = (product: IProductItem) => {
    return +Math.round((product.discount / product.sellPrice) * 100).toFixed(1);
  };

  const renderProduct = (product: IProductItem) => {
    const selected = value.find((s) => s.itemId === product.id);
    const quantityCalc = product.inventory - (selected?.quantity || 0);
    const amountRemaining = (quantityCalc > 0 ? quantityCalc : 0);
    const discountPercent = calcDiscountPercent(product);
    return (
      <ProductCard key={product.id} disabled={!amountRemaining} onClick={onChooseProduct(product)}>
        {!!discountPercent && <DiscountStyled><Text color_text_1 body_body_1_14_medium>{discountPercent}%</Text></DiscountStyled>}
        <ImageContainer>
          <ProductImage
            src={product.urlImage || empty_product_img}
            alt={product.productName}
          />
          {!amountRemaining && (
            <SoldOut color_text_1 content_content_2_16_medium>
              Sold Out
            </SoldOut>
          )}
        </ImageContainer>
        <ProductInfo>
          <Box flex={1}>
            <ProductName text_left content_content_2_16_medium>
              {product.productName}
            </ProductName>
            <ProductQuantity text_left caption_caption_3_12_regular_18>
              {amountRemaining} left
            </ProductQuantity>
          </Box>
          <ProductPriceInfo>
            {renderPrice(product)}
          </ProductPriceInfo>
        </ProductInfo>
      </ProductCard>
    );
  };

  return (
    <>
      <Container style={isLoading ? { display: 'grid' } : { display: 'none' }}>
        <Skeleton.Node style={{ width: '100%', height: '169px' }} active />
        <Skeleton.Node style={{ width: '100%', height: '169px' }} active />
        <Skeleton.Node style={{ width: '100%', height: '169px' }} active />
        <Skeleton.Node style={{ width: '100%', height: '169px' }} active />
        <Skeleton.Node style={{ width: '100%', height: '169px' }} active />
        <Skeleton.Node style={{ width: '100%', height: '169px' }} active />
      </Container>
      <Container style={textSearch ? { display: 'grid' } : { display: 'none' }}>
        {searchProducts.map(renderProduct)}
      </Container>
      <Container style={!textSearch ? { display: 'grid' } : { display: 'none' }}>
        {products.map(renderProduct)}
      </Container>
    </>
  );
};

export default ProductsList;
